import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  MutableRefObject,
} from "react";

import { LazyImage } from "components/Image/Index";

import { a, SpringValue, useSpring, to } from "@react-spring/web";
import { useAnimateInTrail } from "hooks/useAnimateInTrail";
import { initialOffset } from "components/Animations/SpringProperties/SpringProperties";
import { useGesture } from "react-use-gesture";
import * as fromProjects from "store/Projects";

interface IHeroDimensions {
  width: number;
  height: number;
}

interface IProps {
  wrapperRef: MutableRefObject<HTMLDivElement>;
  animateIn: boolean;
  heroSection: fromProjects.IProjectHeroSection;
}

const ThreeFloatingScreenshotsComponent: React.FC<IProps> = ({
  wrapperRef,
  animateIn,
  heroSection,
  ...props
}) => {
  const heroProperties: fromProjects.IProjectHeroSectionProperties =
    heroSection.properties as fromProjects.IProjectHeroSectionProperties;
  const [heroDimensions, setHeroDimensions] = useState<IHeroDimensions>({
    width: 1000,
    height: 5000,
  });

  useEffect(() => {
    const updateHeroDimensions = () => {
      const wrapperElement = wrapperRef.current;
      setHeroDimensions({
        width: wrapperElement.getBoundingClientRect().width,
        height: wrapperElement.getBoundingClientRect().height,
      });
    };

    updateHeroDimensions();

    // Setup event listeners on initial mount to keep the  dimensions of the hero updated
    window.addEventListener("resize", updateHeroDimensions);

    return () => {
      window.removeEventListener("resize", updateHeroDimensions);
    };
  }, []);

  const [cursorPosition, setCursorPosition] = useSpring(() => ({ xy: [0, 0] }));
  const bind = useGesture(
    {
      onMove: (state) => handleMove(state), // fires on mouse move over the element
    },
    { domTarget: wrapperRef }
  );
  useEffect(bind, [bind]);

  const handleMove = (state) => {
    const { xy } = state;
    setCursorPosition({ xy });
  };

  const smallImageRange = 20;
  const largeImageRange = 40;

  const [animateInTrail] = useAnimateInTrail(animateIn, 2);

  const leftImageStyles = {
    opacity: animateInTrail[1].active,
    transform: to(
      [animateInTrail[1].active, cursorPosition.xy],
      (aIn, xy) =>
        `translateY(calc(-50% + ${initialOffset(aIn as number)} + ( ( ( ${
          xy[1]
        } - ( ${heroDimensions.height} / 2 ) ) / ${
          heroDimensions.height
        } ) * ${smallImageRange}px ) )) translateX(calc(( ( ( ${xy[0]} - ( ${
          heroDimensions.width
        } / 2 ) ) / ${heroDimensions.width} ) * ${smallImageRange}px ) ))`
    ),
    willChange: `opacity, transform`,
  };

  const middleImageStyles = {
    opacity: animateInTrail[0].active,
    transform: to(
      [animateInTrail[0].active, cursorPosition.xy],
      (aIn, xy) =>
        `translateY(calc(-50% + ${initialOffset(aIn as number)} + ( ( ( ${
          xy[1]
        } - ( ${heroDimensions.height} / 2 ) ) / ${
          heroDimensions.height
        } ) * ${largeImageRange}px ) )) translateX(calc(-50% + ( ( ( ${
          xy[0]
        } - ( ${heroDimensions.width} / 2 ) ) / ${
          heroDimensions.width
        } ) * ${largeImageRange}px ) ))`
    ),
    willChange: `opacity, transform`,
  };

  const rightImageStyles = {
    opacity: animateInTrail[1].active,
    transform: to(
      [animateInTrail[1].active, cursorPosition.xy],
      (aIn, xy) =>
        `translateY(calc(-50% + ${initialOffset(aIn as number)} + ( ( ( ${
          xy[1]
        } - ( ${heroDimensions.height} / 2 ) ) / ${
          heroDimensions.height
        } ) * ${smallImageRange}px ) )) translateX(calc(( ( ( ${xy[0]} - ( ${
          heroDimensions.width
        } / 2 ) ) / ${heroDimensions.width} ) * ${smallImageRange}px ) ))`
    ),
    willChange: `opacity, transform`,
  };

  return (
    <div className="threeFloatingScreenshotsWrapper">
      <a.div className="figureWrapper" style={leftImageStyles}>
        <LazyImage
          width="100%"
          heightRatio={heroProperties.images[0].heightRatio}
          src={"/assets/images/work/" + heroProperties.images[0].url}
          alt={heroProperties.images[0].alt}
          fit="contain"
          animateImageIn={true}
          animationType={"fadeIn"}
        />
      </a.div>
      <a.div className="figureWrapper" style={middleImageStyles}>
        <LazyImage
          width="100%"
          heightRatio={heroProperties.images[1].heightRatio}
          src={"/assets/images/work/" + heroProperties.images[1].url}
          alt={heroProperties.images[1].alt}
          fit="contain"
          animateImageIn={true}
          animationType={"fadeIn"}
        />
      </a.div>
      <a.div className="figureWrapper" style={rightImageStyles}>
        <LazyImage
          width="100%"
          heightRatio={heroProperties.images[2].heightRatio}
          src={"/assets/images/work/" + heroProperties.images[2].url}
          alt={heroProperties.images[2].alt}
          fit="contain"
          animateImageIn={true}
          animationType={"fadeIn"}
        />
      </a.div>
    </div>
  );
};

export const ThreeFloatingScreenshots = ThreeFloatingScreenshotsComponent;
